/* ============================================================================
 * Dreame HK bundles and add-ons on the PDP (Convx, 2026-10-22)
 *
 * <convx-buy-options> is rendered by snippets/convx-buy-options.liquid only when the product carries
 * convx.bundles or convx.addons. The snippet holds one JSON block of every variant the page needs
 * (own variants, bundle contents, add-ons) with live prices; this file builds from it:
 *
 *   購買方式  the 單購 row and one row per bundle group. A row is chosen through the theme's own variant
 *            change (an optionValueSelectionChange event, handled by product-info.js), so price,
 *            compare-at, badge, docked bar, gallery and the form id follow as they do for the picker.
 *   加購配件  tick boxes. The main add to cart is untouched (single id, so convx-free-gift.js still nests
 *            the gift). Ticked add-ons go in one /cart/add.js items[] request after it resolves.
 *   查看      quick view of the other item in a bundle: photo, price, first four 產品功能 points
 *            (window.ConvxPdp.keyPoints from convx-pdp.js), 選擇此組合, 前往產品頁.
 *   docked bar choice button and total (markup in snippets/main-product-sticky-cart.liquid).
 *
 * Spec: projects/dreame-hk/html/2026-10-20_pdp-accessories-bundles/build-brief.md (rules 1 to 13).
 * Tracking: dispatches 'convx:buy-track'; assets/ga-custom-events.js sends cx_bundle_select,
 * cx_addon_toggle and cx_bundle_peek.
 * ==========================================================================*/
(function () {
  'use strict';

  if (customElements.get('convx-buy-options')) return;

  var T = {
    wayHead: '購買方式',
    addonHead: '加購配件',
    single: '單購',
    separate: '分開購買',
    save: '立省',
    peek: '查看',
    pick: '選擇此組合',
    picked: '已選擇此組合',
    page: '前往產品頁',
    close: '關閉',
    soldOut: '售罄',
    optional: '可選',
    chipsLabel: '選擇款式',
    items: '件',
    total: '合共',
    addons: '件配件',
    addonError: '配件未能加入購物車，主商品已加入。請稍後再試。'
  };

  var CHECK = '<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEVRON = '<svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3.33L10.67 8 6 12.67" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CLOSE = '<svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

  /* ---------------------------------------------------------------- helpers */
  function esc(t) {
    return String(t == null ? '' : t)
      .split('&').join('&amp;').split('<').join('&lt;').split('>').join('&gt;').split('"').join('&quot;');
  }

  function num(n) {
    return Math.round(n);
  }

  // Cents to the store's own money format (window.moneyFormat, "HK${{amount}}" today).
  function money(cents) {
    var format = window.moneyFormat || 'HK${{amount}}';
    var value = cents / 100;
    function fixed(n, decimals, thousands, point) {
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts.join(point);
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
      switch (key) {
        case 'amount_no_decimals': return fixed(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fixed(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fixed(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fixed(value, 2, '\'', '.');
        default: return fixed(value, 2, ',', '.');
      }
    });
  }

  function big(url, width) {
    return String(url || '').replace(/([?&])width=\d+/, '$1width=' + width);
  }

  function track(name, params) {
    document.dispatchEvent(new CustomEvent('convx:buy-track', { detail: { name: name, params: params } }));
  }

  function hasSections(cart, ids) {
    return !!cart && !!cart.sections && ids.every(function (id) { return typeof cart.sections[id] === 'string'; });
  }

  function postJSON(url, payload) {
    return fetch(url, Object.assign(fetchConfig(), { body: JSON.stringify(payload) })).then(function (r) { return r.json(); });
  }

  /* ------------------------------------------------------------- the element */
  class ConvxBuyOptions extends HTMLElement {
    connectedCallback() {
      if (this.cxReady) return;
      var tag = this.querySelector('script[data-cx-buy-data]');
      if (!tag) return;
      try {
        this.data = JSON.parse(tag.textContent);
      } catch (e) {
        if (window.console) console.error('[convx-buy-options] data block is not valid JSON', e);
        return;
      }
      this.cxReady = true;
      this.vs = this.data.vs || {};
      this.sid = this.data.sid;
      this.formIds = (this.dataset.forms || '').split(/\s+/).filter(Boolean);
      this.order = this.data.order || Object.keys(this.vs).filter(function (id) { return this.vs[id].own; }, this).map(Number);
      this.byOpts = {};
      this.order.forEach(function (id) { this.byOpts[this.vs[id].o.join(',')] = id; }, this);
      this.groups = this.buildGroups();
      this.mainForm = document.getElementById('product-form-' + this.sid);

      var sel = this.readSelected();
      this.state = { sel: sel, ticks: {}, chip: {}, wantGroup: null, error: '', loops: 0 };
      var group = this.groupOf(sel);
      if (group) { this.state.wantGroup = group.key; this.state.chip[group.key] = sel; }

      this.body = document.createElement('div');
      this.body.className = 'cx-body';
      this.errorEl = document.createElement('p');
      this.errorEl.className = 'cx-error';
      this.errorEl.setAttribute('role', 'alert');
      this.errorEl.hidden = true;
      this.appendChild(this.body);
      this.appendChild(this.errorEl);

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      document.addEventListener('afterVariantChanged', this.onAfterVariant.bind(this));
      // Capture phase: runs before product-info.js reads the picker's values, so a model or colour chosen
      // in the picker or the docked bar while a bundle is chosen lands on the matching bundle variant in
      // one request instead of two.
      document.addEventListener('optionValueSelectionChange', this.onOptionChange.bind(this), true);
      document.addEventListener('change', this.onQuantity.bind(this));
      document.addEventListener('click', this.onBarChoice.bind(this));

      patchGift();
      this.normalise();
      this.render();
      this.classList.add('is-ready');
    }

    /* ----------------------------------------------------------- data model */
    nameOf(id) {
      var v = this.vs[id];
      return v ? (v.sn || v.psn || v.pt || v.t) : '';
    }

    readSelected() {
      var input = this.mainForm && this.mainForm.querySelector('input[name="id"]');
      var id = input ? Number(input.value) : 0;
      return this.vs[id] ? id : (this.order[0] || 0);
    }

    // The bundle variants, grouped by label (rule 3). Variants sharing a label are one row.
    buildGroups() {
      var groups = [];
      (this.data.bundles || []).forEach(function (id) {
        var v = this.vs[id];
        if (!v) return;
        var key = v.bl || v.t;
        var group = groups.filter(function (g) { return g.key === key; })[0];
        if (!group) { group = { key: key, ids: [] }; groups.push(group); }
        group.ids.push(id);
      }, this);
      return groups;
    }

    groupOf(id) {
      return this.groups.filter(function (g) { return g.ids.indexOf(id) !== -1; })[0] || null;
    }

    // The variant the picker shows as chosen (rules 1 and 8): the selected variant, or for a hidden one the
    // first visible variant among its contents, else the first available visible variant.
    mainOf(id) {
      var v = this.vs[id];
      if (!v || !v.h) return id;
      var i, c;
      for (i = 0; i < (v.c || []).length; i++) {
        c = this.vs[v.c[i]];
        if (c && c.own && !c.h) return c.id;
      }
      var visible = this.order.filter(function (oid) { return !this.vs[oid].h; }, this);
      var open = visible.filter(function (oid) { return this.vs[oid].a; }, this);
      return open[0] || visible[0] || id;
    }

    // Bundle variants of a group that contain the chosen main variant (rule 3).
    candidates(group, mainId) {
      return group.ids.filter(function (id) { return (this.vs[id].c || []).indexOf(mainId) !== -1; }, this);
    }

    pickCandidate(group, cands) {
      var pref = this.state.chip[group.key];
      if (pref && cands.indexOf(pref) !== -1) return pref;
      var open = cands.filter(function (id) { return this.vs[id].a; }, this);
      return open[0] || cands[0];
    }

    // The content variant that is not the chosen main (rule 4).
    partnerOf(candId, mainId) {
      var c = this.vs[candId].c || [];
      for (var i = 0; i < c.length; i++) if (c[i] !== mainId && this.vs[c[i]]) return c[i];
      return null;
    }

    // Chip names: the content variant that differs between the candidates (rule 3).
    chipLabel(candId, cands) {
      var contents = this.vs[candId].c || [];
      var common = contents.filter(function (cid) {
        return cands.every(function (other) { return (this.vs[other].c || []).indexOf(cid) !== -1; }, this);
      }, this);
      var differing = contents.filter(function (cid) { return common.indexOf(cid) === -1 && this.vs[cid]; }, this);
      return differing.map(function (cid) { return this.vs[cid].t; }, this).join(' + ') || this.vs[candId].t;
    }

    // Separately-bought total: live prices of the contents (rule 5). null when a content could not be read.
    separateTotal(candId) {
      var c = this.vs[candId].c || [];
      if (c.length < 2) return null;
      var sum = 0;
      for (var i = 0; i < c.length; i++) {
        if (!this.vs[c[i]]) return null;
        sum += this.vs[c[i]].p;
      }
      return sum;
    }

    // Add-ons shown and tickable for the chosen main (rule 7).
    addonRows(mainId) {
      var main = this.vs[mainId];
      if (!main || main.ha) return [];
      return (this.data.addons || []).filter(function (id) { return this.vs[id]; }, this);
    }

    tickedIds() {
      var main = this.mainOf(this.state.sel);
      return this.addonRows(main).filter(function (id) {
        return this.state.ticks[id] && this.vs[id].a;
      }, this);
    }

    quantity() {
      var input = this.mainForm && this.mainForm.querySelector('.quantity__input');
      var q = input ? parseInt(input.value, 10) : 1;
      return q > 0 ? q : 1;
    }

    /* ------------------------------------------------------------ rendering */
    thumb(id) {
      var v = this.vs[id];
      return v && v.img ? '<span class="cx-thumb"><img src="' + esc(v.img) + '" alt="" width="48" height="48" loading="lazy"></span>' : '<span class="cx-thumb"></span>';
    }

    view() {
      var st = this.state;
      var sel = st.sel;
      var mainId = this.mainOf(sel);
      var main = this.vs[mainId];
      var mainName = this.nameOf(mainId);
      var picked = this.groupOf(sel);
      var rows = [];
      var chosen = { name: mainName + ' ' + T.single, bar: mainName + ' ' + T.single, id: sel };

      if (this.groups.length) {
        rows.push({
          kind: 'single', key: 'single', name: mainName + ' ' + T.single, price: main ? main.p : 0, thumb: mainId,
          selected: !picked, disabled: false, soldOut: main ? !main.a : false
        });
        this.groups.forEach(function (group, gi) {
          var cands = this.candidates(group, mainId);
          if (!cands.length) return;
          var cand = this.pickCandidate(group, cands);
          var v = this.vs[cand];
          var selected = cands.indexOf(sel) !== -1;
          if (selected) cand = sel, v = this.vs[sel];
          var sum = this.separateTotal(cand);
          var saving = sum !== null ? sum - v.p : 0;
          var partnerId = this.partnerOf(cand, mainId);
          var label = group.key.split('{main}').join(mainName);
          var chips = cands.length > 1 ? cands.map(function (cid) {
            return { id: cid, label: this.chipLabel(cid, cands), on: cid === cand, disabled: !this.vs[cid].a };
          }, this) : [];
          var anyOpen = cands.some(function (cid) { return this.vs[cid].a; }, this);
          var row = {
            kind: 'bundle', key: 'g' + gi, group: group.key, cand: cand, name: label, price: v.p, saving: saving > 0 ? saving : 0,
            sum: saving > 0 ? sum : 0, note: v.bn || '', thumb: cand, selected: selected, soldOut: !anyOpen,
            disabled: !anyOpen && !selected, chips: chips, partner: partnerId
          };
          rows.push(row);
          if (selected) {
            var chip = chips.filter(function (c) { return c.on; })[0];
            chosen = {
              name: label + (chip ? ' (' + chip.label + ')' : ''),
              bar: (partnerId ? '+ ' + this.nameOf(partnerId) : label) + (chip ? ' (' + chip.label + ')' : ''),
              id: cand
            };
          }
        }, this);
      }

      var addons = this.addonRows(mainId).map(function (id) {
        var v = this.vs[id];
        return {
          id: id, name: this.nameOf(id), note: v.bn || '', price: v.p, was: v.cp > v.p ? v.cp : 0,
          thumb: id, on: !!st.ticks[id] && v.a, disabled: !v.a
        };
      }, this);

      var ticked = addons.filter(function (a) { return a.on; });
      var unit = this.vs[sel] ? this.vs[sel].p : 0;
      var qty = this.quantity();
      var total = unit * qty + ticked.reduce(function (s, a) { return s + a.price; }, 0);
      return { mainId: mainId, mainName: mainName, rows: rows, addons: addons, ticked: ticked, chosen: chosen, total: total, qty: qty };
    }

    rowHtml(r, vw) {
      var sid = this.sid;
      var inputId = 'cx-way-' + sid + '-' + r.key;
      var cls = 'cx-row cx-row--way' + (r.selected ? ' is-selected' : '') + (r.disabled ? ' is-disabled' : '');
      var line = '<span class="cx-line"><span class="cx-now">' + esc(money(r.price)) + '</span>' +
        (r.saving ? '<span class="cx-badge">' + T.save + esc(money(r.saving)) + '</span>' : '') +
        (r.soldOut ? '<span class="cx-soldout">' + T.soldOut + '</span>' : '') + '</span>';
      var sep = r.saving ? '<span class="cx-sub cx-sep">' + T.separate + ' <s>' + esc(money(r.sum)) + '</s></span>' : '';
      var note = r.note ? '<span class="cx-sub">' + esc(r.note) + '</span>' : '';
      var peek = '';
      if (r.kind === 'bundle' && r.partner) {
        peek = '<button type="button" class="cx-peek" data-cx-peek="' + esc(r.key) + '" data-cx-cand="' + r.cand + '" data-cx-partner="' + r.partner + '">' +
          T.peek + ' ' + esc(this.nameOf(r.partner)) + CHEVRON + '</button>';
      }
      var chips = '';
      if (r.chips && r.chips.length) {
        chips = '<span class="cx-chips" role="group" aria-label="' + T.chipsLabel + '">' + r.chips.map(function (c) {
          return '<button type="button" class="cx-chip' + (c.on ? ' is-on' : '') + '" id="cx-chip-' + sid + '-' + c.id + '" data-cx-chip="' + c.id +
            '" aria-pressed="' + (c.on ? 'true' : 'false') + '"' + (c.disabled ? ' disabled' : '') + '>' + esc(c.label) + '</button>';
        }).join('') + '</span>';
      }
      return '<label class="' + cls + '" data-cx-row="' + esc(r.key) + '">' +
        '<input class="cx-sr" type="radio" id="' + inputId + '" name="cx-way-' + sid + '" value="' + esc(r.key) + '" data-cx-way="' + esc(r.key) + '"' +
        (r.kind === 'bundle' ? ' data-cx-cand="' + r.cand + '"' : '') + (r.selected ? ' checked' : '') + (r.disabled ? ' disabled' : '') + '>' +
        '<span class="cx-radio" aria-hidden="true"></span>' + this.thumb(r.thumb) +
        '<span class="cx-main"><span class="cx-name">' + esc(r.name) + '</span>' + line + sep + note + peek + '</span>' + chips + '</label>';
    }

    addonHtml(a) {
      var cls = 'cx-row cx-row--check' + (a.on ? ' is-selected' : '') + (a.disabled ? ' is-disabled' : '');
      return '<label class="' + cls + '" data-cx-addon="' + a.id + '">' +
        '<input class="cx-sr" type="checkbox" id="cx-addon-' + this.sid + '-' + a.id + '" value="' + a.id + '" data-cx-addon-input="' + a.id + '"' +
        (a.on ? ' checked' : '') + (a.disabled ? ' disabled' : '') + '>' +
        '<span class="cx-check" aria-hidden="true">' + CHECK + '</span>' + this.thumb(a.thumb) +
        '<span class="cx-main"><span class="cx-name">' + esc(a.name) + '</span>' +
        (a.note ? '<span class="cx-sub">' + esc(a.note) + '</span>' : '') +
        (a.disabled ? '<span class="cx-soldout">' + T.soldOut + '</span>' : '') + '</span>' +
        '<span class="cx-price"><span class="cx-now">' + esc(money(a.price)) + '</span>' +
        (a.was ? '<s class="cx-was">' + esc(money(a.was)) + '</s>' : '') + '</span></label>';
    }

    render() {
      var vw = this.view();
      this.vw = vw;
      var active = document.activeElement;
      var focusKey = null;
      if (active && this.contains(active)) {
        focusKey = active.getAttribute('id');
      }
      var html = '';
      if (vw.rows.length) {
        html += '<fieldset class="cx-set"><legend class="cx-legend">' + T.wayHead + ': <span>' + esc(vw.chosen.name) + '</span></legend>' +
          '<div class="cx-rows" role="radiogroup" aria-label="' + T.wayHead + '">' +
          vw.rows.map(function (r) { return this.rowHtml(r, vw); }, this).join('') + '</div></fieldset>';
      }
      if (vw.addons.length) {
        html += '<fieldset class="cx-set"><legend class="cx-legend">' + T.addonHead + ': <span>' +
          esc(vw.ticked.length ? vw.ticked.map(function (a) { return a.name; }).join('、') : T.optional) + '</span></legend>' +
          '<div class="cx-rows">' + vw.addons.map(this.addonHtml, this).join('') + '</div></fieldset>';
      }
      if (vw.ticked.length) {
        html += '<p class="cx-total">' + T.total + ' ' + (vw.qty + vw.ticked.length) + ' ' + T.items + ' <b>' + esc(money(vw.total)) + '</b></p>';
      }
      this.body.innerHTML = html;
      this.hidden = !html;
      if (focusKey) {
        var again = this.body.querySelector('[id="' + focusKey + '"]');
        if (again && again !== document.activeElement) again.focus({ preventScroll: true });
      }
      this.syncPage(vw);
    }

    // Docked bar choice and total, and the add-to-cart button's total (rules 7 and 9).
    syncPage(vw) {
      var bars = document.querySelectorAll('[data-cx-bar-name]');
      Array.prototype.forEach.call(bars, function (el) { el.textContent = vw.chosen.bar; });
      var metas = document.querySelectorAll('[data-cx-bar-meta]');
      Array.prototype.forEach.call(metas, function (el) {
        el.hidden = !vw.ticked.length;
        el.textContent = vw.ticked.length ? '+ ' + vw.ticked.length + ' ' + T.addons : '';
      });
      var totals = document.querySelectorAll('[data-cx-bar-total]');
      Array.prototype.forEach.call(totals, function (el) {
        var v = this.vs[this.state.sel];
        el.textContent = v && v.a ? '· ' + money(vw.total) : '';
      }, this);

      // The in-page button carries the price in a chip (convx-pdp.js). With add-ons ticked it carries what the tap adds.
      var atc = this.mainForm && this.mainForm.querySelector('button[name="add"]');
      var chip = atc && atc.querySelector('.cx-atc-price');
      if (vw.ticked.length && atc && !atc.disabled) {
        if (!chip) {
          chip = document.createElement('span');
          chip.className = 'cx-atc-price';
          atc.appendChild(chip);
        }
        chip.textContent = money(vw.total);
        chip.setAttribute('data-cx-total', '1');
      } else if (chip && chip.getAttribute('data-cx-total')) {
        chip.removeAttribute('data-cx-total');
        if (window.ConvxPdp && window.ConvxPdp.syncAtcPrice) window.ConvxPdp.syncAtcPrice();
      }
    }

    /* ---------------------------------------------------------- interaction */
    select(id, source, targetEl) {
      var st = this.state;
      if (!this.vs[id]) return;
      var group = this.groupOf(id);
      st.wantGroup = group ? group.key : null;
      if (group) st.chip[group.key] = id;
      this.setError('');
      if (st.sel !== id) {
        st.sel = id;
        this.render();
        this.dispatchChange(id, targetEl);
        this.armResync();
      } else {
        this.render();
      }
    }

    // The theme's own variant change: product-info.js listens for this on document and re-renders the
    // price, compare-at, badge, gallery and form id from the section for these option values.
    dispatchChange(id, targetEl) {
      var target = targetEl || this;
      document.dispatchEvent(new CustomEvent('optionValueSelectionChange', {
        detail: {
          sectionId: this.sid,
          target: target,
          selectedOptionValues: this.vs[id].o.map(String),
          cxHandled: true
        }
      }));
    }

    // If the theme never answers (offline, aborted), settle on whatever the form says.
    armResync() {
      clearTimeout(this.resyncTimer);
      this.resyncTimer = setTimeout(function () {
        var now = this.readSelected();
        if (now !== this.state.sel) { this.state.sel = now; this.render(); }
      }.bind(this), 8000);
    }

    // Deep links (rule 8): ?variant= on a hidden variant that is not a bundle row.
    normalise() {
      var st = this.state;
      var v = this.vs[st.sel];
      if (!v || !v.h || this.groupOf(st.sel)) return;
      var addons = this.data.addons || [];
      (v.c || []).forEach(function (cid) { if (addons.indexOf(cid) !== -1) st.ticks[cid] = true; });
      if (addons.indexOf(st.sel) !== -1) st.ticks[st.sel] = true;
      var main = this.mainOf(st.sel);
      if (main !== st.sel) {
        st.sel = main;
        this.dispatchChange(main, null);
        this.armResync();
      }
    }

    setError(message) {
      this.state.error = message;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    onChange(event) {
      var t = event.target;
      if (!t || !t.matches) return;
      if (t.matches('[data-cx-way]')) {
        var key = t.getAttribute('data-cx-way');
        if (key === 'single') {
          this.select(this.vw.mainId, 'row', t);
          track('cx_bundle_select', { buy_item: this.vw.mainName + ' ' + T.single, buy_action: 'row', product_handle: this.data.handle });
        } else {
          var cand = Number(t.getAttribute('data-cx-cand'));
          this.select(cand, 'row', t);
          var row = this.vw.rows.filter(function (r) { return r.key === key; })[0];
          track('cx_bundle_select', { buy_item: row ? row.name : this.vs[cand].t, buy_action: 'row', product_handle: this.data.handle });
        }
      } else if (t.matches('[data-cx-addon-input]')) {
        var id = Number(t.getAttribute('data-cx-addon-input'));
        this.state.ticks[id] = !!t.checked;
        this.setError('');
        this.render();
        track('cx_addon_toggle', { buy_item: this.nameOf(id), buy_action: t.checked ? 'add' : 'remove', product_handle: this.data.handle });
      }
    }

    onClick(event) {
      var t = event.target;
      if (!t || !t.closest) return;
      var chip = t.closest('[data-cx-chip]');
      if (chip && !chip.disabled) {
        event.preventDefault();
        var cand = Number(chip.getAttribute('data-cx-chip'));
        this.select(cand, 'chip', chip);
        var row = this.vw.rows.filter(function (r) { return r.cand === cand || (r.chips || []).some(function (c) { return c.id === cand; }); })[0];
        track('cx_bundle_select', { buy_item: row ? row.name : this.vs[cand].t, buy_action: 'chip', product_handle: this.data.handle });
        return;
      }
      var peek = t.closest('[data-cx-peek]');
      if (peek) {
        event.preventDefault();
        event.stopPropagation();
        this.openPeek(peek);
      }
    }

    // A model or colour chosen in the picker or the docked bar (capture phase, before product-info.js).
    onOptionChange(event) {
      var d = event.detail;
      if (!d || d.cxHandled || !this.cxReady) return;
      if (d.sectionId !== this.sid && d.sectionId !== 'Sticky-' + this.sid) return;
      var st = this.state;
      if (!st.wantGroup) return;
      var newMain = this.byOpts[(d.selectedOptionValues || []).join(',')];
      if (!newMain) return;
      var group = this.groups.filter(function (g) { return g.key === st.wantGroup; })[0];
      var cands = group ? this.candidates(group, newMain) : [];
      if (!cands.length) { st.wantGroup = null; return; }
      var cand = this.pickCandidate(group, cands);
      if (!this.vs[cand].a) { st.wantGroup = null; return; }
      d.selectedOptionValues = this.vs[cand].o.map(String);
      st.chip[group.key] = cand;
      st.sel = cand;
      this.render();
      this.armResync();
    }

    onAfterVariant(event) {
      var v = event.detail && event.detail.variant;
      if (!v || !this.vs[v.id]) return;
      clearTimeout(this.resyncTimer);
      var st = this.state;
      st.sel = Number(v.id);
      // Safety net: a bundle was wanted, the theme settled on a plain variant that has a bundle row.
      if (st.wantGroup && !this.groupOf(st.sel) && st.loops < 2) {
        var group = this.groups.filter(function (g) { return g.key === st.wantGroup; })[0];
        var cands = group ? this.candidates(group, this.mainOf(st.sel)) : [];
        var cand = cands.length ? this.pickCandidate(group, cands) : null;
        if (cand && this.vs[cand].a) {
          st.loops++;
          this.select(cand, 'picker', null);
          return;
        }
        st.wantGroup = null;
      } else {
        st.loops = 0;
      }
      this.render();
    }

    onQuantity(event) {
      var t = event.target;
      if (t && t.matches && t.matches('.quantity__input') && this.mainForm && this.mainForm.contains(t)) this.render();
    }

    // The docked bar's choice button scrolls back to the choices.
    onBarChoice(event) {
      var t = event.target;
      if (!t || !t.closest || !t.closest('[data-cx-bar-choice]')) return;
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* ----------------------------------------------------- add-ons to the cart */
    handlesForm(form) {
      var id = form && form.getAttribute && form.getAttribute('id');
      return !!id && this.formIds.indexOf(id) !== -1;
    }

    // Runs after the main add (and the gift nest) resolved (rule 7). The main item is already in the cart,
    // so any failure here returns the main response untouched and shows a short message.
    addAddons(main, url, formData, ids) {
      if (!ids.length || !main || main.status) return main;
      // cart.getSectionsToRender() repeats ids (cart-drawer three times); send each once, as convx-free-gift.js does
      var sections = String(formData.get('sections') || '').split(',').map(function (s) { return s.trim(); })
        .filter(function (s, i, all) { return s && all.indexOf(s) === i; });
      var payload = { items: ids.map(function (id) { return { id: Number(id), quantity: 1 }; }) };
      if (sections.length) {
        payload.sections = sections;
        payload.sections_url = formData.get('sections_url') || window.location.pathname;
      }
      var self = this;
      return postJSON(url + '.js', payload)
        .then(function (res) {
          if (!res || res.status) throw new Error((res && (res.description || res.message)) || 'add-on add failed');
          self.setError('');
          if (!sections.length || hasSections(res, sections)) return Object.assign({}, main, { sections: res.sections || main.sections });
          // A cart mutation can answer with "sections": null; a no-op update re-renders (see convx-free-gift.js).
          // The add-ons are already in the cart here, so a failed re-render is not an add-on failure: keep the
          // main response's sections and show no error (QA 23 Oct, N4).
          return postJSON(routes.cart_update_url, { sections: sections, sections_url: payload.sections_url }).then(function (fresh) {
            return Object.assign({}, main, { sections: hasSections(fresh, sections) ? fresh.sections : main.sections });
          }, function () { return main; });
        })
        .catch(function (error) {
          if (window.console) console.error('[convx-buy-options] add-ons not added', error);
          self.setError(T.addonError);
          return main;
        });
    }

    /* ------------------------------------------------------------ quick view */
    ensureSheet() {
      if (this.sheet) return this.sheet;
      var sheet = document.createElement('div');
      sheet.className = 'cx-sheet';
      sheet.hidden = true;
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
      sheet.setAttribute('aria-labelledby', 'cxSheetTitle-' + this.sid);
      sheet.innerHTML =
        '<button type="button" class="cx-sheet__scrim" tabindex="-1" aria-label="' + T.close + '"></button>' +
        '<div class="cx-sheet__panel">' +
        '<button type="button" class="cx-sheet__close" aria-label="' + T.close + '">' + CLOSE + '</button>' +
        '<img class="cx-sheet__media" alt="" src="data:,">' +
        '<h3 class="cx-sheet__title" id="cxSheetTitle-' + this.sid + '"></h3>' +
        '<div class="cx-sheet__price"></div>' +
        '<ul class="cx-sheet__points" hidden></ul>' +
        '<div class="cx-sheet__actions"><a class="cx-sheet__page" href="#">' + T.page + '</a>' +
        '<button type="button" class="cx-sheet__pick">' + T.pick + '</button></div></div>';
      document.body.appendChild(sheet);
      var self = this;
      sheet.querySelector('.cx-sheet__scrim').addEventListener('click', function () { self.closePeek('dismiss'); });
      sheet.querySelector('.cx-sheet__close').addEventListener('click', function () { self.closePeek('dismiss'); });
      sheet.querySelector('.cx-sheet__page').addEventListener('click', function () { self.peekEvent('page'); });
      sheet.querySelector('.cx-sheet__pick').addEventListener('click', function () {
        var cand = self.peek && self.peek.cand;
        if (!cand) return;
        // already the chosen bundle: nothing to change, the button just closes the sheet
        if (self.state.sel !== cand) {
          self.select(cand, 'quick_view', null);
          var row = self.vw.rows.filter(function (r) { return r.cand === cand; })[0];
          track('cx_bundle_select', { buy_item: row ? row.name : self.vs[cand].t, buy_action: 'quick_view', product_handle: self.data.handle });
        }
        self.closePeek('select');
      });
      sheet.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        var items = Array.prototype.filter.call(sheet.querySelectorAll('button, a[href]'), function (el) { return !el.disabled && el.getAttribute('tabindex') !== '-1'; });
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !sheet.hidden) self.closePeek('dismiss');
      });
      this.sheet = sheet;
      return sheet;
    }

    peekEvent(action) {
      if (!this.peek) return;
      track('cx_bundle_peek', { buy_item: this.nameOf(this.peek.partner), buy_action: action, product_handle: this.data.handle });
    }

    // The partner's description comes from /products/<handle>.js, once per handle.
    loadProduct(handle) {
      this.products = this.products || {};
      if (!this.products[handle]) {
        this.products[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      }
      return this.products[handle];
    }

    openPeek(button) {
      var sheet = this.ensureSheet();
      var cand = Number(button.getAttribute('data-cx-cand'));
      var partnerId = Number(button.getAttribute('data-cx-partner'));
      var partner = this.vs[partnerId];
      if (!partner) return;
      this.peek = { key: button.getAttribute('data-cx-peek'), cand: cand, partner: partnerId };
      this.peekButton = button;
      var media = sheet.querySelector('.cx-sheet__media');
      media.src = big(partner.img, 800);
      media.alt = partner.pt;
      sheet.querySelector('.cx-sheet__title').textContent = partner.pt;
      var price = '<span class="cx-now">' + esc(money(partner.p)) + '</span>';
      if (partner.cp > partner.p) {
        price += '<s class="cx-was">' + esc(money(partner.cp)) + '</s><span class="cx-badge">' + T.save + esc(money(partner.cp - partner.p)) + '</span>';
      }
      sheet.querySelector('.cx-sheet__price').innerHTML = price;
      var points = sheet.querySelector('.cx-sheet__points');
      points.hidden = true;
      points.innerHTML = '';
      sheet.querySelector('.cx-sheet__page').setAttribute('href', partner.pu + '?variant=' + partner.id);
      var pick = sheet.querySelector('.cx-sheet__pick');
      var already = this.state.sel === cand;
      pick.textContent = already ? T.picked : T.pick;

      sheet.hidden = false;
      document.documentElement.classList.add('cx-sheet-open');
      requestAnimationFrame(function () { sheet.classList.add('is-open'); });
      sheet.querySelector('.cx-sheet__close').focus();
      this.peekEvent('open');

      var self = this;
      var peekAtOpen = this.peek;
      this.loadProduct(partner.ph).then(function (product) {
        if (!product || self.peek !== peekAtOpen || !window.ConvxPdp || !window.ConvxPdp.keyPoints) return;
        var lines = window.ConvxPdp.keyPoints(product.description, 4, product.title);
        if (!lines.length) return;
        points.innerHTML = lines.map(function (line) {
          return '<li>' + CHECK.replace('width="12" height="12"', 'width="16" height="16"') + '<span>' + esc(line) + '</span></li>';
        }).join('');
        points.hidden = false;
      });
    }

    closePeek(action) {
      var sheet = this.sheet;
      if (!sheet || sheet.hidden) return;
      this.peekEvent(action);
      var key = this.peek && this.peek.key;
      this.peek = null;
      sheet.classList.remove('is-open');
      document.documentElement.classList.remove('cx-sheet-open');
      setTimeout(function () { if (!sheet.classList.contains('is-open')) sheet.hidden = true; }, 230);
      // the row may have been rebuilt while the sheet was open: find the link again by its row key
      var link = key ? this.querySelector('[data-cx-peek="' + key + '"]') : null;
      if (link) link.focus({ preventScroll: true });
    }
  }

  /* ------------------------------------------- add-ons ride the main add to cart */
  // product-form.js hands its /cart/add request to ConvxFreeGift.add (the gift nests under the main line
  // there). Wrapping it keeps the main request unchanged and lets the add-ons go in right after, before
  // product-form.js renders the drawer, so the drawer renders once with everything in it.
  // This file is deferred from the buy box, which comes before convx-free-gift.js (end of <body>), so
  // ConvxFreeGift does not exist yet when it runs (23 Oct: add-ons never reached the cart). Patch on
  // DOMContentLoaded and again on the submit itself (capture phase, before product-form.js's handler on
  // the form). The marker sits on the function, so a replaced ConvxFreeGift is patched again.
  function patchGift() {
    var gift = window.ConvxFreeGift;
    if (!gift || typeof gift.add !== 'function' || gift.add.cxAddons) return;
    var original = gift.add;
    gift.add = function (url, config, formData, form) {
      var base = original.apply(this, arguments);
      var el = document.querySelector('convx-buy-options');
      if (!el || !el.cxReady || !el.handlesForm(form)) return base;
      var ids = el.tickedIds();
      if (!ids.length) return base;
      return base.then(function (main) { return el.addAddons(main, url, formData, ids); });
    };
    gift.add.cxAddons = true;
  }
  document.addEventListener('DOMContentLoaded', patchGift);
  document.addEventListener('submit', patchGift, true);

  customElements.define('convx-buy-options', ConvxBuyOptions);
  patchGift();
})();
